import type { ExcelDownloadClient } from "app/components/React/ExcelButton/ExcelClientDownload";
import type {
    ContactsStatus,
    ContactsTabCountriesDto,
    EmployeeProfilesDescriptionDto,
} from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/types";
import { createCancelable } from "app/pages/workspace/sales/helpers";
import type { GetRequestOptions, IFetchService, PostConfigObject } from "app/services/fetchService";

import type {
    ContactRevealByIdRequestDto,
    ContactsExportRequestDto,
    ContactsSearchResponseWithTotalCountDto,
    ContactsSearchResultsRequestDto,
    RevealedContactDto,
    RevealedContactsResponseDto,
    TopNRevealRequestDto,
} from "../../contacts-search/types/dto";

const BASE_URL = "/sales-api/contacts";

export const createContactsApiService = (fetchService: IFetchService, excelDownloadClient: ExcelDownloadClient) => {
    return {
        fetchContactsStatus: createCancelable((signal: AbortSignal): Promise<ContactsStatus> => {
            return fetchService.get(`${BASE_URL}/status`, undefined, {
                cancellation: signal,
            });
        }),
        acceptContactsTerms(): Promise<void> {
            return fetchService.post(`${BASE_URL}/terms`);
        },
        fetchContactsDataWithTotalCount(
            dto: ContactsSearchResultsRequestDto,
            config?: PostConfigObject,
        ): Promise<ContactsSearchResponseWithTotalCountDto> {
            return fetchService.post(`${BASE_URL}/search`, dto, config);
        },
        fetchCountries(
            dto: ContactsSearchResultsRequestDto,
            config?: PostConfigObject,
        ): Promise<ContactsTabCountriesDto> {
            return fetchService.post(`${BASE_URL}/count-by-countries`, dto, config);
        },

        revealContactsById(contacts: readonly ContactRevealByIdRequestDto[]): Promise<RevealedContactsResponseDto> {
            return fetchService.post(`${BASE_URL}/reveal/identity`, contacts);
        },
        revealStatus(requestId: string): Promise<number> {
            return fetchService.get(`${BASE_URL}/reveal/status`, { requestId });
        },
        revealSingleContact(contact: ContactRevealByIdRequestDto): Promise<RevealedContactDto> {
            return fetchService.post(`${BASE_URL}/reveal/single`, contact);
        },
        revealTopNContacts(dto: TopNRevealRequestDto): Promise<RevealedContactsResponseDto> {
            return fetchService.post(`${BASE_URL}/reveal`, dto);
        },
        exportToExcel(dto: ContactsExportRequestDto) {
            return excelDownloadClient(`${BASE_URL}/excel`, dto);
        },
        fetchContactsDescription(options?: GetRequestOptions): Promise<EmployeeProfilesDescriptionDto> {
            return fetchService.get(`${BASE_URL}/description`, undefined, options);
        },
    };
};
